import type { AppLocale } from '@/shared/configs/app.config';
import type { CartResult, QuoteRequestResult } from '@/shared/types/fetch.types';
import { getApiUrl, makeZodSafeFetchWithRefreshToken } from '@/shared/utils/common.utils';
import { cartResultSchema, quoteRequestResultSchema } from '@/shared/validation/fetch.validation';
import type { QuoteFormValues } from '@/shared/validation/form.validation';

const jsonHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

// `GET /config/{language}/cart` get-or-creates the active cart and sets the
// `configurator_cart_token` cookie when needed — there is no separate "create"
// call any more.
export const getCart = async (locale: AppLocale): Promise<CartResult> => {
  return await makeZodSafeFetchWithRefreshToken(getApiUrl('/cart', locale), cartResultSchema, {
    credentials: 'include',
  });
};

export const removeConfigFromCart = async (
  locale: AppLocale,
  configCode: string,
): Promise<CartResult> => {
  const url = getApiUrl(`/cart/configs/${encodeURIComponent(configCode)}`, locale);

  return await makeZodSafeFetchWithRefreshToken(url, cartResultSchema, {
    method: 'DELETE',
    credentials: 'include',
    headers: jsonHeaders,
  });
};

export const updateCart = async (locale: AppLocale, discount: number): Promise<CartResult> => {
  return await makeZodSafeFetchWithRefreshToken(getApiUrl('/cart', locale), cartResultSchema, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ discount }),
  });
};

export const submitQuoteRequest = async (
  locale: AppLocale,
  values: QuoteFormValues,
  preview = false,
): Promise<QuoteRequestResult | string> => {
  // Pimcore names this endpoint "checkout"; the word is Pimcore's. In this codebase the act is a
  // Quote Request (see GLOSSARY.md) — nothing is purchased or paid for.
  const url = getApiUrl(`/cart/checkout${preview ? '?preview=true' : ''}`, locale);

  const body = JSON.stringify({
    ...values,
    marketingEmail: values.marketingEmail === 'yes',
    marketingPhone: values.marketingPhone === 'yes',
  });

  if (preview) {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    return await res.text();
  }

  const result = await makeZodSafeFetchWithRefreshToken(url, quoteRequestResultSchema, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body,
  });

  if (result.error) {
    throw new Error(result.error ?? 'Quote request failed');
  }

  return result;
};
